import { useState } from "react";
import { Check, PencilLine, Sparkles, ThumbsDown, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";
import { draftRecommendation } from "@/lovable-demo/lib/warranty/ai.functions";
import { logAiInteraction } from "@/lovable-demo/lib/governance/ai-governance";
import type {
  ProblemRecord,
  Recommendation,
  RecommendationDecision,
} from "@/lovable-demo/lib/warranty/types";

const urgencyTone: Record<string, string> = {
  now: "border-destructive/40 bg-destructive/12 text-destructive",
  "this-week": "border-warning/40 bg-warning/12 text-warning",
  scheduled: "border-info/40 bg-info/12 text-info",
};

const urgencyLabel: Record<string, string> = {
  now: "act now",
  "this-week": "this week",
  scheduled: "scheduled",
};

export function RecommendationCard({
  record,
  rec,
  decision,
}: {
  record: ProblemRecord;
  rec: Recommendation;
  decision?: RecommendationDecision | undefined;
}) {
  const { decideRecommendation } = useWarranty();
  const [mode, setMode] = useState<"idle" | "modify" | "reject">("idle");
  const [draft, setDraft] = useState(rec.action);
  const [reason, setReason] = useState("");
  const [aiText, setAiText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  if (decision) {
    const tone =
      decision.verdict === "rejected"
        ? "border-destructive/40 bg-destructive/8"
        : decision.verdict === "modified"
          ? "border-warning/40 bg-warning/8"
          : "border-success/40 bg-success/8";
    return (
      <div className={cn("rounded border p-3", tone)}>
        <div className="flex items-start gap-2">
          <Sparkles className="mt-0.5 size-3.5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Human decision · {decision.verdict}
            </p>
            <p className="mt-1 text-sm text-foreground">{decision.finalAction}</p>
            {decision.note && (
              <p className="mt-1 text-xs text-muted-foreground">Reviewer: {decision.note}</p>
            )}
            <p className="num mt-1 text-[0.66rem] text-muted-foreground">
              {decision.decidedBy} · {fmtDate(decision.at)} · {rec.clause}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Controlled governance event: the decision only counts as recorded once the
  // record, its audit entry and the Evidence Snapshot at Decision are confirmed.
  const commit = async (
    verdict: "accepted" | "modified" | "rejected",
    action: string,
    note: string,
  ) => {
    setSaving(true);
    try {
      const result = await decideRecommendation(record.id, rec, verdict, action, note);
      if (!result.ok) {
        toast.error("Decision not recorded", { description: result.error });
        return;
      }
      setMode("idle");
      setReason("");
      toast.success(
        verdict === "accepted"
          ? "Recommendation accepted"
          : verdict === "modified"
            ? "Recommendation accepted with changes"
            : "Recommendation rejected",
        { description: "Confirmed in the append-only audit trail." },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded border border-primary/35 bg-primary/[0.06] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded border border-primary/40 bg-primary/12 px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider text-primary">
          <Sparkles className="size-3" /> Recommendation
        </span>
        <span
          className={cn(
            "rounded border px-1.5 py-0.5 text-[0.62rem] font-semibold uppercase tracking-wider",
            urgencyTone[rec.urgency],
          )}
        >
          {urgencyLabel[rec.urgency]}
        </span>
        <span className="num ml-auto text-[0.66rem] text-muted-foreground">
          confidence {rec.confidence}%
        </span>
      </div>

      <p className="mt-2 text-sm font-medium text-foreground">{rec.title}</p>
      <p className="mt-1 text-sm text-foreground/90">{rec.action}</p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{rec.rationale}</p>

      {rec.impact && <p className="mt-1.5 text-[0.7rem] text-primary">{rec.impact}</p>}

      <div className="mt-2 flex flex-wrap gap-1.5">
        {rec.basis.map((b) => (
          <span
            key={b}
            className="num rounded border border-border/70 bg-elevated/60 px-1.5 py-0.5 text-[0.62rem] text-muted-foreground"
          >
            {b}
          </span>
        ))}
      </div>

      {aiText && (
        <p className="mt-2 rounded border border-border/70 bg-elevated/50 p-2 text-xs leading-relaxed text-foreground/90">
          {aiText}
        </p>
      )}

      {mode === "idle" && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={saving}
            onClick={() => void commit("accepted", rec.action, "")}
          >
            <Check className="size-3" /> {saving ? "Recording…" : "Accept"}
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => {
              setDraft(rec.action);
              setMode("modify");
            }}
          >
            <PencilLine className="size-3" /> Modify
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs text-destructive"
            onClick={() => setMode("reject")}
          >
            <ThumbsDown className="size-3" /> Reject
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-primary"
            disabled={aiBusy}
            onClick={async () => {
              setAiBusy(true);
              try {
                const res = await draftRecommendation({
                  data: {
                    title: rec.title,
                    action: rec.action,
                    rationale: rec.rationale,
                    context: `Record ${record.code}: ${record.title}. Component ${record.component}. Supplier ${record.supplier}. Plant ${record.plant}. Risk ${record.risk} (${record.riskScore}). Claims ${record.claims}. Exposure $${record.financial.exposure}. Stage ${rec.stageIndex}. Basis: ${rec.basis.join("; ")}.`,
                  },
                });
                // AI governance: logged as advisory only; the authorised
                // decision remains the human verdict recorded below.
                logAiInteraction({
                  recordId: record.id,
                  recordCode: record.code,
                  stageId: rec.stageId,
                  recommendationId: rec.id,
                  model: "deterministic-demo",
                  promptVersion: "decision-brief-v1",
                  inputSummary: rec.title,
                  outputSummary: res.text,
                  outcome: res.error ? "failed" : "drafted",
                  error: res.error,
                });
                if (res.error) toast.error(res.error);
                else setAiText(res.text);
              } catch {
                toast.error(
                  "The synthetic brief could not be prepared. The recommendation above still stands.",
                );
              } finally {
                setAiBusy(false);
              }
            }}
          >
            <Sparkles className="size-3" /> {aiBusy ? "Drafting…" : "Synthetic decision brief"}
          </Button>
        </div>
      )}

      {mode === "modify" && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="min-h-20 bg-card text-xs"
          />
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you changing it? (recorded in the audit trail)"
            className="min-h-14 bg-card text-xs"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={!draft.trim() || saving}
              onClick={() => void commit("modified", draft.trim(), reason.trim())}
            >
              <Check className="size-3" /> {saving ? "Recording…" : "Save decision"}
            </Button>

            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setMode("idle")}
            >
              <X className="size-3" /> Cancel
            </Button>
          </div>
        </div>
      )}

      {mode === "reject" && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason for rejecting (required — recorded in the audit trail)"
            className="min-h-16 bg-card text-xs"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs text-destructive"
              disabled={!reason.trim() || saving}
              onClick={() => void commit("rejected", `Rejected: ${rec.action}`, reason.trim())}
            >
              <ThumbsDown className="size-3" /> {saving ? "Recording…" : "Confirm rejection"}
            </Button>

            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={() => setMode("idle")}
            >
              <X className="size-3" /> Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
